import { FC, MouseEvent, ReactNode, useMemo } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { DatePicker, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { ForecastingRow } from '@/api/forecasting';
import type { DashboardFilters } from '../forecasting.types';
import {
  formatFyLabel,
  getFyDateRange,
  toIsoDate,
  uniqueFilterOptions
} from '../utils/forecastingAggregations';
import { hasActiveSidebarFilters, isDateRangeCustom } from '../utils/filterHelpers';
import { FIRST_DATA_MONTH, fyCodesInRange, getCurrentFyCode } from '../utils/fyCalendar';
import {
  ClearAllFiltersButton,
  FilterClearButton,
  FilterDateHint,
  FilterLabel,
  FilterLabelRow,
  FilterOptionRow,
  FilterPopupActions,
  Sidebar
} from '../Forecasting.styles';

const { RangePicker } = DatePicker;

interface FilterSidebarProps {
  filters: DashboardFilters;
  allRows: ForecastingRow[];
  fyOptions: { label: string; value: string }[];
  defaultDateRange: Pick<DashboardFilters, 'fy' | 'dateFrom' | 'dateTo'>;
  onChange: (patch: Partial<DashboardFilters>) => void;
  onClearAll: () => void;
}

const selectProps = {
  allowClear: true,
  showSearch: true,
  size: 'small' as const,
  style: { width: '100%' }
};

/* Optional filters take several values, e.g. two salespeople at once. Tags
   collapse to "+N" so a long pick does not grow the sidebar. */
const multiSelectProps = {
  ...selectProps,
  mode: 'multiple' as const,
  maxTagCount: 'responsive' as const
};

/** An emptied selection is no filter, not a filter matching nothing. */
const nonEmpty = (values: string[]) => (values.length ? values : undefined);

/* Stops the popup closing, or the input blurring, before a click inside it lands. */
const keepOpen = (event: MouseEvent) => event.preventDefault();

/**
 * A multi-value filter with Looker Studio's shortcuts: "Select all" at the top
 * of the list, and "Only" beside each option to pick it alone without first
 * clearing everything else.
 */
function MultiFilter({
  options,
  value,
  onChange
}: {
  options: { label: string; value: string }[];
  value?: string[];
  onChange: (values: string[] | undefined) => void;
}) {
  const every = options.map((option) => option.value);
  const allSelected = every.length > 0 && every.every((v) => value?.includes(v));

  return (
    <Select
      {...multiSelectProps}
      options={options}
      value={value}
      onChange={(v: string[]) => onChange(nonEmpty(v))}
      popupRender={(menu) => (
        <>
          <FilterPopupActions>
            <button
              type="button"
              onMouseDown={keepOpen}
              onClick={() => onChange(allSelected ? undefined : every)}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          </FilterPopupActions>
          {menu}
        </>
      )}
      optionRender={(option) => (
        <FilterOptionRow>
          <span>{option.label}</span>
          <button
            type="button"
            className="filter-only"
            onMouseDown={keepOpen}
            onClick={(event) => {
              // Picks this option alone, rather than toggling it as the row would.
              event.stopPropagation();
              onChange([String(option.value)]);
            }}
          >
            Only
          </button>
        </FilterOptionRow>
      )}
    />
  );
}

function toOptions(values: string[]) {
  return values.map((v) => ({ label: v, value: v }));
}

function FilterField({
  label,
  clearable,
  onClear,
  children
}: {
  label: string;
  clearable?: boolean;
  onClear?: () => void;
  children: ReactNode;
}) {
  return (
    <div>
      <FilterLabelRow>
        <FilterLabel>{label}</FilterLabel>
        {clearable && onClear && (
          <FilterClearButton type="button" onClick={onClear} aria-label={`Clear ${label}`}>
            <CloseOutlined />
          </FilterClearButton>
        )}
      </FilterLabelRow>
      {children}
    </div>
  );
}

export const FilterSidebar: FC<FilterSidebarProps> = ({
  filters,
  allRows,
  fyOptions,
  defaultDateRange,
  onChange,
  onClearAll
}) => {
  const rangeValue: [Dayjs, Dayjs] | null = useMemo(() => {
    if (!filters.dateFrom || !filters.dateTo) return null;
    return [dayjs(filters.dateFrom), dayjs(filters.dateTo)];
  }, [filters.dateFrom, filters.dateTo]);

  const opts = useMemo(
    () => ({
      channel: toOptions(uniqueFilterOptions(allRows, 'Channel')),
      sales: toOptions(uniqueFilterOptions(allRows, 'Sales')),
      buyingPoint: toOptions(uniqueFilterOptions(allRows, 'Buying_Point')),
      am: toOptions(uniqueFilterOptions(allRows, 'AM')),
      advertiser: toOptions(uniqueFilterOptions(allRows, 'Advertiser')),
      status: toOptions(uniqueFilterOptions(allRows, 'Status')),
      source: toOptions(uniqueFilterOptions(allRows, 'Source')),
      clientType: toOptions(uniqueFilterOptions(allRows, 'Customer_Type')),
      newExisting: toOptions(uniqueFilterOptions(allRows, 'New_Existing')),
      territory: toOptions(uniqueFilterOptions(allRows, 'Territory')),
      platform: toOptions(uniqueFilterOptions(allRows, 'Platform'))
    }),
    [allRows]
  );

  const rangeFys = fyCodesInRange(filters.dateFrom, filters.dateTo);
  const rangeFyLabel =
    rangeFys.length > 1
      ? `${formatFyLabel(rangeFys[0])} – ${formatFyLabel(rangeFys[rangeFys.length - 1])}`
      : formatFyLabel(filters.fy);

  const dateRangeCustom = isDateRangeCustom(filters, defaultDateRange.dateFrom, defaultDateRange.dateTo);
  const showClearAll = hasActiveSidebarFilters(filters, defaultDateRange);

  const handleFyChange = (fy: string) => {
    const range = getFyDateRange(fy);
    if (!range) {
      onChange({ fy });
      return;
    }
    onChange({
      fy,
      dateFrom: toIsoDate(range.start),
      dateTo: toIsoDate(range.end)
    });
  };

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates[1]) return;
    const start = dates[0].startOf('month');
    const end = dates[1].endOf('month');
    /* The FY follows the range, so a range moved into another year names
       that year rather than the one it was picked from. */
    onChange({
      fy: getCurrentFyCode(end.toDate()),
      dateFrom: start.format('YYYY-MM-DD'),
      dateTo: end.format('YYYY-MM-DD')
    });
  };

  const clearDateRange = () => {
    onChange({
      fy: defaultDateRange.fy,
      dateFrom: defaultDateRange.dateFrom,
      dateTo: defaultDateRange.dateTo
    });
  };

  return (
    <Sidebar className="forecasting-filters">
      {showClearAll && (
        <ClearAllFiltersButton type="button" onClick={onClearAll}>
          Clear filters
        </ClearAllFiltersButton>
      )}

      <FilterField label="Financial year">
        <Select
          {...selectProps}
          allowClear={false}
          options={fyOptions}
          value={filters.fy}
          onChange={(fy) => handleFyChange(fy)}
        />
      </FilterField>

      <FilterField label="Date range" clearable={dateRangeCustom} onClear={clearDateRange}>
        <RangePicker
          size="small"
          picker="month"
          value={rangeValue}
          format="MMM-YY"
          style={{ width: '100%' }}
          disabledDate={(d) => d.isBefore(FIRST_DATA_MONTH, 'month')}
          onChange={handleRangeChange}
        />
        {filters.dateFrom && filters.dateTo && (
          <FilterDateHint
            title={`${rangeFyLabel} · ${dayjs(filters.dateFrom).format('D MMM YYYY')} – ${dayjs(filters.dateTo).format('D MMM YYYY')}`}
          >
            {rangeFyLabel} · {dayjs(filters.dateFrom).format('MMM-YY')} –{' '}
            {dayjs(filters.dateTo).format('MMM-YY')}
          </FilterDateHint>
        )}
      </FilterField>

      <FilterField
        label="Channel"
        clearable={!!filters.channel?.length}
        onClear={() => onChange({ channel: undefined })}
      >
        <MultiFilter options={opts.channel} value={filters.channel} onChange={(channel) => onChange({ channel })} />
      </FilterField>

      <FilterField label="Sales" clearable={!!filters.sales?.length} onClear={() => onChange({ sales: undefined })}>
        <MultiFilter options={opts.sales} value={filters.sales} onChange={(sales) => onChange({ sales })} />
      </FilterField>

      <FilterField
        label="Buying Point"
        clearable={!!filters.buyingPoint?.length}
        onClear={() => onChange({ buyingPoint: undefined })}
      >
        <MultiFilter options={opts.buyingPoint} value={filters.buyingPoint} onChange={(buyingPoint) => onChange({ buyingPoint })} />
      </FilterField>

      <FilterField label="AM" clearable={!!filters.am?.length} onClear={() => onChange({ am: undefined })}>
        <MultiFilter options={opts.am} value={filters.am} onChange={(am) => onChange({ am })} />
      </FilterField>

      <FilterField
        label="Advertiser"
        clearable={!!filters.advertiser?.length}
        onClear={() => onChange({ advertiser: undefined })}
      >
        <MultiFilter options={opts.advertiser} value={filters.advertiser} onChange={(advertiser) => onChange({ advertiser })} />
      </FilterField>

      <FilterField label="Status" clearable={!!filters.status?.length} onClear={() => onChange({ status: undefined })}>
        <MultiFilter options={opts.status} value={filters.status} onChange={(status) => onChange({ status })} />
      </FilterField>

      <FilterField label="Source" clearable={!!filters.source?.length} onClear={() => onChange({ source: undefined })}>
        <MultiFilter options={opts.source} value={filters.source} onChange={(source) => onChange({ source })} />
      </FilterField>

      <FilterField
        label="Client Type"
        clearable={!!filters.clientType?.length}
        onClear={() => onChange({ clientType: undefined })}
      >
        <MultiFilter options={opts.clientType} value={filters.clientType} onChange={(clientType) => onChange({ clientType })} />
      </FilterField>

      <FilterField
        label="New / Existing"
        clearable={!!filters.newExisting?.length}
        onClear={() => onChange({ newExisting: undefined })}
      >
        <MultiFilter options={opts.newExisting} value={filters.newExisting} onChange={(newExisting) => onChange({ newExisting })} />
      </FilterField>

      <FilterField
        label="Territory"
        clearable={!!filters.territory?.length}
        onClear={() => onChange({ territory: undefined })}
      >
        <MultiFilter options={opts.territory} value={filters.territory} onChange={(territory) => onChange({ territory })} />
      </FilterField>

      <FilterField
        label="Platform"
        clearable={!!filters.platform?.length}
        onClear={() => onChange({ platform: undefined })}
      >
        <MultiFilter options={opts.platform} value={filters.platform} onChange={(platform) => onChange({ platform })} />
      </FilterField>
    </Sidebar>
  );
};
